import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, HelpCircle, Search, ArrowRight } from 'lucide-react';
import { fetchCollection, COMPANY_INFO } from '../data';
import DynamicScrollbar from '../components/DynamicScrollbar';

export default function FAQ() {
  const [faqs, setFaqs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [openFaqIdx, setOpenFaqIdx] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadFaqs() {
      try {
        setLoading(true);
        const fetchedFaqs = await fetchCollection('faqs');
        setFaqs(fetchedFaqs.sort((a, b) => (a.order || 99) - (b.order || 99)));
      } catch (error) {
        console.error('Error loading FAQs:', error);
      } finally {
        setLoading(false);
      }
    }
    loadFaqs();
    window.scrollTo(0, 0);
  }, []);

  const filteredFaqs = faqs.filter(
    (faq) =>
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-brand-dark/50 pt-20">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-brand-blue border-t-transparent mb-4" />
        <p className="text-base">Indlæser ofte stillede spørgsmål...</p>
      </div>
    );
  }

  return (
    <div className="pt-20 space-y-16">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-brand-light via-white to-white py-16 sm:py-20 overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center space-y-6">
          <div className="flex flex-wrap items-center justify-center gap-2">
            <span className="text-xs font-bold tracking-widest text-brand-blue uppercase bg-brand-light px-3 py-1 rounded-full">
              FAQ
            </span>
            <span className="text-xs font-semibold text-brand-deep bg-brand-light/60 border border-brand-light px-3 py-1 rounded-full">
              Base i Smørum – dækker Egedal, Nordsjælland og store dele af Sjælland
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-brand-deep leading-tight tracking-tight">
            Ofte stillede spørgsmål (FAQ)
          </h1>
          <p className="text-base sm:text-lg text-brand-dark/80 leading-relaxed max-w-2xl mx-auto">
            Her finder du uddybende svar på alt om energiruder, termoruder, dug, kondens og glarmesterarbejde fra <Link to="/" className="text-brand-deep hover:text-brand-blue font-semibold underline decoration-brand-blue/30 underline-offset-2 transition-colors">Energirude Eksperten</Link>. Med base i Smørum dækker vi Egedal, Nordsjælland og store dele af Sjælland.
          </p>

          {/* Search Bar */}
          <div className="max-w-md mx-auto relative pt-4">
            <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none text-brand-dark/40 pt-4">
              <Search className="w-5 h-5" />
            </div>
            <input
              type="text"
              placeholder="Søg i spørgsmål og svar..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-brand-light bg-white text-brand-dark placeholder-brand-dark/40 focus:outline-none focus:border-brand-blue focus:ring-1 focus:ring-brand-blue shadow-sm transition-all text-sm sm:text-base"
            />
          </div>
        </div>
      </section>

      {/* Dynamic Scrollbar */}
      <DynamicScrollbar />

      {/* FAQ List */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {filteredFaqs.length > 0 ? (
          <div className="space-y-4">
            {filteredFaqs.map((faq, idx) => {
              const isOpen = openFaqIdx === idx;
              return (
                <div
                  key={faq.id}
                  className="bg-white border border-brand-light rounded-xl overflow-hidden shadow-sm transition-all"
                >
                  <button
                    onClick={() => setOpenFaqIdx(isOpen ? null : idx)}
                    className="flex items-center justify-between w-full p-5 text-left font-bold text-brand-deep hover:text-brand-blue transition-colors gap-4"
                  >
                    <span className="text-sm sm:text-base">{faq.question}</span>
                    <ChevronDown
                      className={`w-5 h-5 text-brand-blue shrink-0 transition-transform duration-200 ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5 pt-0 border-t border-brand-light/50">
                      <div
                        className="text-sm sm:text-base text-brand-dark/80 leading-relaxed pt-4 prose max-w-none"
                        dangerouslySetInnerHTML={{ __html: faq.answer }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12 text-brand-dark/50">
            <HelpCircle className="w-12 h-12 mx-auto text-brand-dark/30 mb-3" />
            <p className="text-sm sm:text-base">Ingen resultater matchede din søgning.</p>
          </div>
        )}
      </section>

      {/* CTA Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="bg-brand-deep text-white rounded-3xl p-8 sm:p-12 lg:p-16 relative overflow-hidden border border-brand-blue/20 shadow-xl text-center space-y-6">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(36,119,199,0.15),transparent_60%)] pointer-events-none" />
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight max-w-3xl mx-auto leading-tight">
            Fandt du ikke svar på dit spørgsmål?
          </h2>
          <p className="text-sm sm:text-base text-brand-light/80 max-w-2xl mx-auto">
            Du er altid velkommen til at kontakte os direkte. Vores glarmester hos <Link to="/" className="text-white hover:text-brand-light underline font-semibold">Energirude Eksperten</Link> har base i Smørum og yder gratis og uforpligtende rådgivning om ruder i Egedal, Nordsjælland og store dele af Sjælland.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
            <Link
              to="/kontakt"
              className="w-full sm:w-auto bg-brand-blue hover:bg-white hover:text-brand-deep text-white px-8 py-4 rounded-xl font-bold transition-all duration-200 shadow-md"
            >
              Kontakt os nu
            </Link>
            <a
              href={`tel:${COMPANY_INFO.phone.replace(/\s+/g, '')}`}
              className="w-full sm:w-auto border border-white/20 hover:bg-white/10 text-white px-8 py-4 rounded-xl font-bold transition-all duration-200 flex items-center justify-center gap-2"
            >
              Ring på {COMPANY_INFO.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
